import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type State = "loading" | "ready" | "done" | "invalid" | "error";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Avregistrera utskick — Fenny" },
      {
        name: "description",
        content:
          "Avsluta prenumerationen på utskick från Fenny. Bekräfta med ett klick så slutar vi mejla dig.",
      },
      { property: "og:title", content: "Avregistrera utskick — Fenny" },
      {
        property: "og:description",
        content: "Avsluta prenumerationen på utskick från Fenny med ett klick.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/unsubscribe" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/unsubscribe" }],
  }),
});

function UnsubscribePage() {
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [state, setState] = useState<State>("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    setToken(t);
    if (!t) {
      setState("invalid");
      return;
    }
    fetch(`/api/public/unsubscribe?token=${encodeURIComponent(t)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!d?.valid) {
          setState("invalid");
          return;
        }
        setEmail(d.email ?? null);
        setState(d.alreadyDone ? "done" : "ready");
      })
      .catch(() => setState("error"));
  }, []);

  async function confirm() {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch("/api/public/unsubscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const d = await res.json();
      setState(d?.ok ? "done" : "error");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-xl flex-col justify-center px-6 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Fenny</p>
      <h1 className="mt-3 text-3xl font-semibold text-foreground">Avregistrera utskick</h1>

      {state === "loading" && (
        <p className="mt-6 text-muted-foreground">Hämtar din prenumeration …</p>
      )}

      {state === "ready" && (
        <>
          <p className="mt-6 text-muted-foreground">
            Vill du avsluta utskicken till{" "}
            <span className="font-medium text-foreground">{email}</span>?
          </p>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="mt-6 w-fit rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
          >
            {busy ? "Avregistrerar …" : "Bekräfta avregistrering"}
          </button>
        </>
      )}

      {state === "done" && (
        <p className="mt-6 text-muted-foreground">
          Klart — vi skickar inga fler utskick{email ? ` till ${email}` : ""}.
        </p>
      )}

      {(state === "invalid" || state === "error") && (
        <>
          <p className="mt-6 text-muted-foreground">
            {state === "invalid"
              ? "Länken är ogiltig eller för gammal."
              : "Något gick fel när vi skulle avregistrera dig."}{" "}
            Använd avregistreringslänken längst ned i mejlet du fick från oss.
          </p>
          <p className="mt-4 text-muted-foreground">
            Behöver du hjälp? Mejla{" "}
            <a className="underline" href="mailto:hey@fenny.se">
              hey@fenny.se
            </a>{" "}
            så fixar vi det direkt.
          </p>
        </>
      )}
    </main>
  );
}
